import cls from 'classnames';
import React, { Fragment, PureComponent, RefObject } from 'react';
import { IFilterOption } from 'core/redux/features/shared/types';
import classNames from 'classnames';
import { TransFn } from 'core/redux/features/languages/trans';

interface PropTypes {
  className?: string;
  isActive?: boolean;
  options: Array<IFilterOption>;
  onSelect: (e: React.SyntheticEvent, item: IFilterOption) => void;
  selected: IFilterOption;
  deviceType?: string;
  activeOnHover?: boolean;
  trans: TransFn;
  controlMobile: boolean;
  iosIviFix?: boolean;
  defaultLabel?: string;
  hideInviSelect?: boolean;
  closeOnSelect?: boolean;
}

interface State {
  active?: boolean;
}

interface IDropdownProps {
  isActive?: boolean;
  label?: string;
  children: React.ReactNode;
  className?: string;
  forwardRef?: RefObject<HTMLDivElement>;
  buttonChild?: React.ReactNode;
  onActive?: () => void;
  activeOnHover?: boolean;
  activeOnClick?: boolean;
  hideLabel?: boolean;
}

export class DropDownButton extends PureComponent<
  IDropdownProps,
  { active: boolean }
> {
  static defaultProps = {
    isActive: false,
    activeOnHover: true,
    hideLabel: false,
  };

  state = {
    active: false,
  };

  private timeOutMouseLeave: any;

  render() {
    const {
      props: { isActive, label, children, className, forwardRef, hideLabel },
      state: { active },
    } = this;

    const classStr = classNames(
      'dropdown',
      active || isActive ? ' is-active' : '',
      className,
    );

    return (
      <div
        className={classStr}
        onMouseOver={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        ref={forwardRef}
      >
        <div className="dropdown-trigger">
          {!hideLabel && (
            <a
              className="button"
              aria-haspopup="true"
              aria-controls="dropdown-menu"
            >
              <span>{label}</span>
              {this.props.buttonChild}
            </a>
          )}
        </div>
        {children}
      </div>
    );
  }

  private onActive = (toggle: boolean) => {
    if (this.state.active !== toggle) {
      this.setState({ active: toggle });

      const { onActive } = this.props;
      if (onActive) {
        onActive();
      }
    }
  };

  private handleClick = () => {
    clearTimeout(this.timeOutMouseLeave);
    this.onActive(true);
  };

  private handleMouseEnter = () => {
    if (this.props.activeOnHover) {
      clearTimeout(this.timeOutMouseLeave);
      this.onActive(true);
    }
  };

  private onMouseLeave = () => {
    if (this.timeOutMouseLeave) {
      clearTimeout(this.timeOutMouseLeave);
    }

    return setTimeout(() => {
      this.onActive(false);
    }, 300);
  };

  private handleMouseLeave = () => {
    this.timeOutMouseLeave = this.onMouseLeave();
  };
}

export default class ButtonSelectDropdown extends PureComponent<
  PropTypes,
  State
> {
  static defaultProps = {
    deviceType: 'desktop',
    activeOnHover: true,
    trans: (key, _) => key,
    controlMobile: true,
    iosIviFix: false,
  };

  state = {
    active: false,
  };

  render() {
    const {
      props: {
        selected,
        options,
        deviceType,
        isActive,
        onSelect,
        activeOnHover,
        trans,
        controlMobile,
        iosIviFix,
        defaultLabel,
        hideInviSelect,
      },
    } = this;

    let selectedOp: IFilterOption = selected;
    // if no selected, we will use the first option
    if (!selectedOp) {
      selectedOp = options[0];
    }

    const selectedOpDetails = options.find(op => op.id === selectedOp.id);
    let label = defaultLabel;
    const labelKey =
      defaultLabel ||
      selectedOpDetails?.display_label ||
      selectedOpDetails?.label;
    if (trans && labelKey) {
      label = trans(labelKey);
    }

    return (
      <Fragment>
        <select
          style={{ display: hideInviSelect ? 'none' : '' }}
          value={selected.id}
          className={`invi-select${iosIviFix ? ' invi-select--ios-fix' : ''}`}
          onChange={this.onChangeSelect}
        >
          {options.map((item, key) => (
            <option key={key} value={item.id} disabled={item.disabled ?? false}>
              {trans(item.label)}
            </option>
          ))}
        </select>
        <DropDownButton
          activeOnHover={activeOnHover}
          buttonChild={this.props.children}
          isActive={isActive}
          className={this.props.className}
          label={label}
        >
          <div
            className={classNames(
              'dropdown-menu',
              controlMobile && deviceType === 'mobile' && 'is-control-mobile',
              deviceType === 'mobile' && controlMobile && 'hide',
            )}
          >
            <div className="dropdown-content">
              {options.map((item, key) => (
                <a
                  href={item.route || '#'}
                  onClick={e => {
                    if (item.disabled) {
                      e.preventDefault();
                      return;
                    }

                    onSelect(e, item);
                  }}
                  key={key}
                  className={cls(
                    'dropdown-item',
                    item.disabled && 'is-disabled',
                    selectedOp.id === item.id && 'is-active',
                  )}
                >
                  {trans(item.label)}
                </a>
              ))}
            </div>
          </div>
        </DropDownButton>
      </Fragment>
    );
  }

  private onActive = (toggle: boolean) => {
    if (this.state.active !== toggle) {
      this.setState({ active: toggle });
    }
  };

  private onChangeSelect = (e: React.FormEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget;
    const item = this.props.options.find(op => {
      if (typeof op.id === 'number') {
        return op.id === parseInt(value);
      }
      return op.id === value;
    });

    if (item) {
      this.props.onSelect(e, item);
    }
  };
}
